export const resolveGroupDeviceIds = (group) =>
  Array.isArray(group?.devices) ? group.devices : [];

export const resolveGroupDevices = (connectedDevices, group) => {
  if (!Array.isArray(connectedDevices) || !group) return [];
  const idSet = new Set(resolveGroupDeviceIds(group));
  return connectedDevices.filter((device) => idSet.has(device.id));
};

/**
 * History rows for the group modal, flattened newest-first from the devices'
 * live data. Each row is stamped with its owning device's node_id: data points
 * carry `uid`/`did` but not `node_id`, so the history's Device ID column was
 * blank without it. Stage devices are excluded (their points are sensor
 * readings, not events) unless `includeStage` — the per-device history view
 * shows whatever the selected device has.
 */
export const buildGroupHistoryRows = (devices, { includeStage = false } = {}) =>
  (Array.isArray(devices) ? devices : [])
    .filter((device) => includeStage || device?.device_type !== 'Stage')
    .flatMap((device) =>
      (Array.isArray(device?.data) ? device.data : []).map((point) => ({
        ...point,
        node_id: point?.node_id ?? device?.node_id,
      })),
    )
    .sort((nextEntry, prevEntry) => (prevEntry?.ts ?? 0) - (nextEntry?.ts ?? 0));

// The controls need an initial status row; displayed history always reads live members.
export const buildGroupCommandDraft = (device, groupType, history) => {
  if (!device) return null;
  return {
    ...device,
    device_type: groupType || device.device_type || '',
    data: history.slice(0, 1),
  };
};

const SIREN_DEVICE_TYPE = 'Technology Bridge_Siren/Giant Voice';

export const isSirenDevice = (device) => device?.device_type === SIREN_DEVICE_TYPE;

/** The four fields the backend reads; nothing else travels (`device_ids` is the server's to check). */
export const audioForGroupCommand = (audio) => {
  if (!audio || typeof audio !== 'object') return null;
  return {
    id: audio.id ?? null,
    relay_id: audio.relay_id ?? null,
    name: audio.name ?? null,
    displayName: audio.displayName ?? audio.name ?? null,
  };
};

/**
 * The body extras for `POST /device-group/:id/command`: interval, repeat,
 * volume as before, plus — for sirens — the group's audio file and one
 * `targets` entry per siren that plays its own (`perDeviceAudio` is
 * `{ [deviceId]: audio | null }`, null meaning "same as group"). Audio is sent
 * only for an alerting command: idle plays nothing, and the backend records
 * a file only when one is played.
 */
export const buildGroupCommandArgs = (deviceState, perDeviceAudio = null) => {
  const commandArgs = {};
  const intervalValue = Number(deviceState?.interval);
  if (Number.isFinite(intervalValue)) commandArgs.interval = intervalValue;
  const repeatValue = Number(deviceState?.repeat);
  if (Number.isFinite(repeatValue)) commandArgs.repeat = repeatValue;
  const volumeValue = Number(deviceState?.volume);
  if (Number.isFinite(volumeValue)) commandArgs.volume = volumeValue;
  // An active override (DeviceControls sends only that) bypasses each member's maintenance
  // mode. The backend accepts it from a maintenance operator only.
  if (deviceState?.maintenanceOverride === true) commandArgs.maintenanceOverride = true;

  const alerting = Number(deviceState?.status) > 0;
  if (!alerting) return commandArgs;

  const groupAudio = audioForGroupCommand(deviceState?.audio);
  if (groupAudio) commandArgs.audio = groupAudio;

  const targets = Object.entries(perDeviceAudio || {})
    .filter(([, audio]) => audio)
    .map(([deviceId, audio]) => ({
      device_id: Number(deviceId),
      audio: audioForGroupCommand(audio),
    }))
    .filter((t) => Number.isInteger(t.device_id) && t.device_id > 0);
  if (targets.length > 0) commandArgs.targets = targets;
  return commandArgs;
};
